import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Landmark,
    ShieldCheck,
    Plus,
    Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
    }).format(amount);

const WealthPortfolio = () => {
    const [assets, setAssets] = useState([]);
    const [liabilities, setLiabilities] = useState([]);
    const [emis, setEmis] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal States
    const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
    const [isLiabilityModalOpen, setIsLiabilityModalOpen] = useState(false);
    const [isEmiModalOpen, setIsEmiModalOpen] = useState(false);

    // Form States
    const [formData, setFormData] = useState({});

    // Simulator State
    const [simValues, setSimValues] = useState({ amount: '', tenureMonths: '', interestRate: '' });
    const [simResult, setSimResult] = useState(null);
    const [simLoading, setSimLoading] = useState(false);

    const fetchPortfolio = useCallback(async () => {
        try {
            const config = getAuthConfig();
            const [assetsRes, liabilitiesRes, emisRes] = await Promise.all([
                axios.get(`${API}/financial-health/assets`, config),
                axios.get(`${API}/financial-health/liabilities`, config),
                axios.get(`${API}/financial-health/emis`, config),
            ]);

            if (assetsRes.data.success) setAssets(assetsRes.data.data);
            if (liabilitiesRes.data.success) setLiabilities(liabilitiesRes.data.data);
            if (emisRes.data.success) setEmis(emisRes.data.data);
        } catch (error) {
            console.error('Failed to fetch portfolio', error);
            toast.error("Failed to load portfolio data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPortfolio();
    }, [fetchPortfolio]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCreate = async (type) => {
        // Basic client-side validation
        if (!formData.name) {
            toast.error('Name is required');
            return;
        }

        try {
            const config = getAuthConfig();
            let endpoint = '';

            if (type === 'asset') endpoint = `${API}/financial-health/assets`;
            if (type === 'liability') endpoint = `${API}/financial-health/liabilities`;
            if (type === 'emi') endpoint = `${API}/financial-health/emis`;

            await axios.post(endpoint, formData, config);

            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} added successfully`);

            if (type === 'asset') setIsAssetModalOpen(false);
            if (type === 'liability') setIsLiabilityModalOpen(false);
            if (type === 'emi') setIsEmiModalOpen(false);

            setFormData({});
            fetchPortfolio();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to create item';
            toast.error(message);
        }
    };

    const runSimulation = async (e) => {
        e.preventDefault();
        setSimLoading(true);
        try {
            const config = getAuthConfig();
            const response = await axios.post(`${API}/financial-health/simulate`, simValues, config);
            setSimResult(response.data.data);
        } catch (error) {
            if (error.response?.status === 403) {
                toast.warning('Premium Feature: Please upgrade to use the Simulator.');
            } else {
                toast.error('Simulation Failed');
            }
        } finally {
            setSimLoading(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-96"><Loader2 className="h-8 w-8 animate-spin" /></div>;
    }

    const totalAssets = assets.reduce((sum, a) => sum + Number(a.value), 0);
    const totalLiabilities = liabilities.reduce((sum, l) => sum + Number(l.outstandingAmount), 0);
    const netWorth = totalAssets - totalLiabilities;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight">Wealth Portfolio</h2>
                    <p className="text-muted-foreground mt-1">Manage your assets, liabilities, and EMI commitments.</p>
                </div>
                <Button onClick={fetchPortfolio} variant="outline" size="sm">
                    Refresh
                </Button>
            </div>

            {/* Net Worth Summary */}
            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Total Assets</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-green-600">{formatCurrency(totalAssets)}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Total Liabilities</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-red-600">{formatCurrency(totalLiabilities)}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Net Worth</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className={`text-2xl font-bold ${netWorth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(netWorth)}
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Tabs defaultValue="portfolio" className="w-full">
                <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
                    <TabsTrigger value="portfolio"><Landmark className="mr-2 h-4 w-4" /> Portfolio</TabsTrigger>
                    <TabsTrigger value="simulator"><ShieldCheck className="mr-2 h-4 w-4" /> Simulator</TabsTrigger>
                </TabsList>

                {/* --- PORTFOLIO TAB --- */}
                <TabsContent value="portfolio" className="space-y-4 pt-4">
                    <Tabs defaultValue="assets" className="w-full">
                        <TabsList>
                            <TabsTrigger value="assets">Assets ({assets.length})</TabsTrigger>
                            <TabsTrigger value="liabilities">Liabilities ({liabilities.length})</TabsTrigger>
                            <TabsTrigger value="emis">Active EMIs ({emis.length})</TabsTrigger>
                        </TabsList>

                        {/* ASSETS TABLE */}
                        <TabsContent value="assets" className="space-y-4">
                            <div className="flex justify-end">
                                <Dialog open={isAssetModalOpen} onOpenChange={setIsAssetModalOpen}>
                                    <DialogTrigger asChild>
                                        <Button><Plus className="mr-2 h-4 w-4" /> Add Asset</Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Add New Asset</DialogTitle>
                                        </DialogHeader>
                                        <div className="grid gap-4 py-4">
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="name" className="text-right">Name</Label>
                                                <Input id="name" name="name" onChange={handleInputChange} className="col-span-3" placeholder="e.g. HDFC FD" />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="type" className="text-right">Type</Label>
                                                <Select onValueChange={(v) => handleSelectChange('type', v)}>
                                                    <SelectTrigger className="col-span-3"><SelectValue placeholder="Select Type" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="bank">Bank Balance</SelectItem>
                                                        <SelectItem value="investment">Investment</SelectItem>
                                                        <SelectItem value="fd">Fixed Deposit</SelectItem>
                                                        <SelectItem value="real_estate">Real Estate</SelectItem>
                                                        <SelectItem value="cash">Cash</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="value" className="text-right">Value (₹)</Label>
                                                <Input id="value" name="value" type="number" onChange={handleInputChange} className="col-span-3" />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="growthRate" className="text-right">Growth %</Label>
                                                <Input id="growthRate" name="growthRate" type="number" onChange={handleInputChange} className="col-span-3" />
                                            </div>
                                        </div>
                                        <Button onClick={() => handleCreate('asset')}>Save Asset</Button>
                                    </DialogContent>
                                </Dialog>
                            </div>
                            <Card>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Value</TableHead>
                                            <TableHead>Growth %</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {assets.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                                                    No assets added yet.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            assets.map((asset) => (
                                                <TableRow key={asset.id}>
                                                    <TableCell className="font-medium">{asset.name}</TableCell>
                                                    <TableCell><Badge variant="outline">{asset.type}</Badge></TableCell>
                                                    <TableCell>{formatCurrency(Number(asset.value))}</TableCell>
                                                    <TableCell>{asset.growthRate}%</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Card>
                        </TabsContent>

                        {/* LIABILITIES TABLE */}
                        <TabsContent value="liabilities" className="space-y-4">
                            <div className="flex justify-end">
                                <Dialog open={isLiabilityModalOpen} onOpenChange={setIsLiabilityModalOpen}>
                                    <DialogTrigger asChild>
                                        <Button variant="destructive"><Plus className="mr-2 h-4 w-4" /> Add Liability</Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Add Liability</DialogTitle>
                                        </DialogHeader>
                                        <div className="grid gap-4 py-4">
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="lname" className="text-right">Name</Label>
                                                <Input id="lname" name="name" onChange={handleInputChange} className="col-span-3" placeholder="e.g. Home Loan" />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="ltype" className="text-right">Type</Label>
                                                <Select onValueChange={(v) => handleSelectChange('type', v)}>
                                                    <SelectTrigger className="col-span-3"><SelectValue placeholder="Select Type" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="loan">Loan</SelectItem>
                                                        <SelectItem value="credit_card">Credit Card</SelectItem>
                                                        <SelectItem value="borrowing">Personal Borrowing</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="ltotal" className="text-right">Total (₹)</Label>
                                                <Input id="ltotal" name="totalAmount" type="number" onChange={handleInputChange} className="col-span-3" />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="lout" className="text-right">Outstanding (₹)</Label>
                                                <Input id="lout" name="outstandingAmount" type="number" onChange={handleInputChange} className="col-span-3" />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="lint" className="text-right">Interest %</Label>
                                                <Input id="lint" name="interestRate" type="number" onChange={handleInputChange} className="col-span-3" />
                                            </div>
                                        </div>
                                        <Button onClick={() => handleCreate('liability')}>Save Liability</Button>
                                    </DialogContent>
                                </Dialog>
                            </div>
                            <Card>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Outstanding</TableHead>
                                            <TableHead>Interest %</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {liabilities.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                                                    No liabilities recorded.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            liabilities.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell className="font-medium">{item.name}</TableCell>
                                                    <TableCell><Badge variant="secondary">{item.type}</Badge></TableCell>
                                                    <TableCell>{formatCurrency(Number(item.outstandingAmount))}</TableCell>
                                                    <TableCell>{item.interestRate}%</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Card>
                        </TabsContent>

                        {/* EMIS TABLE */}
                        <TabsContent value="emis" className="space-y-4">
                            <div className="flex justify-end">
                                <Dialog open={isEmiModalOpen} onOpenChange={setIsEmiModalOpen}>
                                    <DialogTrigger asChild>
                                        <Button><Plus className="mr-2 h-4 w-4" /> Add EMI</Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Add Active EMI</DialogTitle>
                                        </DialogHeader>
                                        <div className="grid gap-4 py-4">
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="ename" className="text-right">Name</Label>
                                                <Input id="ename" name="name" onChange={handleInputChange} className="col-span-3" placeholder="e.g. Car Loan EMI" />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="eamount" className="text-right">Amount (₹)</Label>
                                                <Input id="eamount" name="amount" type="number" onChange={handleInputChange} className="col-span-3" />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="edue" className="text-right">Due Date</Label>
                                                <Input id="edue" name="dueDate" type="number" min="1" max="31" onChange={handleInputChange} className="col-span-3" placeholder="Day of month (1-31)" />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="etenure" className="text-right">Tenure Left</Label>
                                                <Input id="etenure" name="tenureMonths" type="number" onChange={handleInputChange} className="col-span-3" placeholder="Months" />
                                            </div>
                                        </div>
                                        <Button onClick={() => handleCreate('emi')}>Save EMI</Button>
                                    </DialogContent>
                                </Dialog>
                            </div>
                            <Card>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Amount</TableHead>
                                            <TableHead>Due Date</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {emis.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">
                                                    No active EMIs.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            emis.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell className="font-medium">{item.name}</TableCell>
                                                    <TableCell>{formatCurrency(Number(item.amount))}</TableCell>
                                                    <TableCell>{item.dueDate}th of month</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </TabsContent>

                {/* --- SIMULATOR TAB --- */}
                <TabsContent value="simulator" className="space-y-4 pt-4">
                    <div className="grid gap-4 md:grid-cols-2">
                        <Card>
                            <CardHeader>
                                <CardTitle>New Purchase Simulator</CardTitle>
                                <CardDescription>Check if you can afford a new loan or EMI</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={runSimulation} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="simAmount">Loan/Purchase Amount (₹)</Label>
                                        <Input
                                            id="simAmount"
                                            value={simValues.amount}
                                            onChange={(e) => setSimValues({ ...simValues, amount: e.target.value })}
                                            type="number"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="simTenure">Tenure (Months)</Label>
                                        <Input
                                            id="simTenure"
                                            value={simValues.tenureMonths}
                                            onChange={(e) => setSimValues({ ...simValues, tenureMonths: e.target.value })}
                                            type="number"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="simInterest">Interest Rate (%)</Label>
                                        <Input
                                            id="simInterest"
                                            value={simValues.interestRate}
                                            onChange={(e) => setSimValues({ ...simValues, interestRate: e.target.value })}
                                            type="number"
                                            required
                                        />
                                    </div>
                                    <Button type="submit" className="w-full" disabled={simLoading}>
                                        {simLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                        Analyze Impact
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>

                        {simResult && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Analysis Result</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="text-center">
                                        <div className="text-sm text-muted-foreground">New Estimated EMI</div>
                                        <div className="text-3xl font-bold">{formatCurrency(Number(simResult.newEMI))}</div>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span>Projected EMI Ratio</span>
                                            <span className={Number(simResult.newEMIRatio) > 40 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'}>
                                                {simResult.newEMIRatio}%
                                            </span>
                                        </div>
                                        <Progress value={Number(simResult.newEMIRatio)} className="h-2" />
                                    </div>

                                    <Alert variant={simResult.verdict === 'Safe' ? 'default' : 'destructive'} className={simResult.verdict === 'Safe' ? 'border-green-600 bg-green-50' : ''}>
                                        <AlertTitle className="text-lg">Verdict: {simResult.verdict}</AlertTitle>
                                        <AlertDescription>
                                            {simResult.verdict === 'Safe' ? 'This purchase fits within your healthy financial limits.' :
                                                simResult.verdict === 'Risky' ? 'This will push your EMI burden high. Proceed with caution.' :
                                                    'This purchase is dangerous for your financial health.'}
                                        </AlertDescription>
                                    </Alert>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default WealthPortfolio;
